import classNames from "classnames"
import { Badge, Icon, Text } from "@oysterhr/ui"
import { PayoutViewState, Maybe, PaymentTimelineEvent, PaymentTimelineItem } from "@oysterhr/schema"
import { OysterIcon } from "@oysterhr/icons"
import { DateTime } from "luxon"
import type { TFunction } from "react-i18next"

interface PayoutTimelineItemProps {
  item: PaymentTimelineItem
  t: TFunction
}

interface PayoutTimelineItemDetailProps {
  event: PaymentTimelineEvent
  t: TFunction
  index: number
  bankAccount?: Maybe<string>
}

export const PayoutTimelineItemDetail = ({
  event,
  bankAccount,
  index,
  t,
}: PayoutTimelineItemDetailProps) => {
  return (
    <div className="flex items-center space-x-2" data-testid="payout-timeline-item-detail">
      <Icon
        className={classNames("", {
          "text-success-base":
            index > 0 || event.name === PayoutViewState.Sent || event.name === PayoutViewState.Paid,
          "text-error-base": index === 0 && event.name === PayoutViewState.Returned,
          "text-info-darken-100":
            (index === 0 && event.name === PayoutViewState.AutomatedProcessInProgress) ||
            event.name === PayoutViewState.Initiated,
        })}
        data-testid={`event-${event.name}`}
        name={getEventIcon(index, event.name)}
        size="sm"
      />
      <div className="flex flex-col justify-center">
        {event.completedAt && (
          <Text className="font-semibold">
            {DateTime.fromISO(event.completedAt).toLocaleString(DateTime.DATETIME_MED)}
          </Text>
        )}
        <Text className="text-gray-400">
          {t(`drawer.event.${event.name}`, {
            accountNumber: bankAccount,
          })}
        </Text>
      </div>
    </div>
  )
}

export const PayoutTimelineItemSimplified = ({ item, t }: PayoutTimelineItemProps) => (
  <div className="flex items-center justify-between border-t pt-4">
    <Text className="font-semibold">
      {DateTime.fromISO(item.events?.[0].completedAt).toLocaleString(DateTime.DATETIME_MED)}
    </Text>
    <Badge variation={getBadgeVariation(item.status)}>
      {t(`payoutStates.${item.status.toLowerCase()}.title`)}
    </Badge>
  </div>
)

const getEventIcon = (eventIndex: number, eventName?: Maybe<PayoutViewState>): OysterIcon => {
  // Events are sorted in the BE, the first item on the list is the most recent
  const isLatest = eventIndex === 0
  if (!isLatest) return "CheckCircle"

  switch (eventName) {
    case PayoutViewState.Returned:
      return "InfoCircle"
    case PayoutViewState.AutomatedProcessInProgress:
    case PayoutViewState.Initiated:
      return "SpinnerGap"
    default:
      return "CheckCircle"
  }
}

const getBadgeVariation = (eventStatus: PayoutViewState) => {
  switch (eventStatus) {
    case PayoutViewState.Returned:
      return "error"
    case PayoutViewState.Sent:
    case PayoutViewState.Paid:
      return "success"
    default:
      return "gray"
  }
}
